/**
 * PlaybookOrb - Animated colored orb icon for Playbooks
 * Each playbook gets unique colors based on its ID
 * Creates a diffuse, heatmap-like appearance with slow organic movement
 */

import React, { useMemo } from 'react';
import './PlaybookOrb.scss';

interface PlaybookOrbProps {
  entityId: string;
  size?: number;
}

/**
 * Generate unique color palette from entity ID hash
 */
function generateColorsFromId(id: string): [string, string, string] {
  // Simple hash function
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = ((hash << 5) - hash) + id.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  // Generate 3 distinct hues from hash
  const hue1 = Math.abs(hash % 360);
  const hue2 = (hue1 + 120) % 360;
  const hue3 = (hue1 + 240) % 360;
  
  // High saturation and medium-high lightness for vibrant colors
  const saturation = 70 + (Math.abs(hash >> 8) % 20); // 70-90%
  const lightness = 55 + (Math.abs(hash >> 16) % 15);  // 55-70%
  
  return [
    `hsl(${hue1}, ${saturation}%, ${lightness}%)`,
    `hsl(${hue2}, ${saturation}%, ${lightness}%)`,
    `hsl(${hue3}, ${saturation}%, ${lightness}%)`
  ];
}

export const PlaybookOrb: React.FC<PlaybookOrbProps> = ({ entityId, size = 16 }) => {
  const [color1, color2, color3] = useMemo(() => generateColorsFromId(entityId), [entityId]);
  
  // Slower animation durations for more organic feel
  const durations = useMemo(() => {
    let hash = 0;
    for (let i = 0; i < entityId.length; i++) {
      hash = ((hash << 5) - hash) + entityId.charCodeAt(i);
    }
    return {
      main: 8 + (Math.abs(hash) % 4),        // 8-12 seconds
      blob1: 10 + (Math.abs(hash >> 8) % 5), // 10-15 seconds
      blob2: 9 + (Math.abs(hash >> 16) % 6), // 9-15 seconds
      blob3: 11 + (Math.abs(hash >> 24) % 4) // 11-15 seconds
    };
  }, [entityId]);
  
  return (
    <div 
      className="playbook-orb" 
      style={{ 
        width: size, 
        height: size,
        '--orb-color-1': color1,
        '--orb-color-2': color2,
        '--orb-color-3': color3,
      } as React.CSSProperties}
    >
      <svg width={size} height={size} viewBox="0 0 100 100">
        <defs>
          {/* Heavy blur filter for diffuse heatmap effect */}
          <filter id={`blur-${entityId}`} x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="15" />
          </filter>
          
          {/* Even heavier blur for outer glow */}
          <filter id={`glow-${entityId}`} x="-150%" y="-150%" width="400%" height="400%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="25" />
          </filter>
        </defs>
        
        {/* Outer glow base */}
        <circle 
          cx="50" 
          cy="50" 
          r="30" 
          fill={color1}
          opacity="0.3"
          filter={`url(#glow-${entityId})`}
        />
        
        {/* Subtle boundary circle */}
        <circle 
          cx="50" 
          cy="50" 
          r="35" 
          fill="none"
          stroke="currentColor"
          strokeWidth="0.5"
          opacity="0.15"
          className="playbook-orb__boundary"
        />
        
        {/* Multiple diffuse color blobs that move around */}
        <g filter={`url(#blur-${entityId})`}>
          {/* Blob 1 - color 1 */}
          <circle 
            cx="50" 
            cy="50" 
            r="20" 
            fill={color1}
            opacity="0.8"
            className="playbook-orb__blob"
            style={{
              '--blob-duration': `${durations.blob1}s`,
              '--blob-offset': '0s'
            } as React.CSSProperties}
          />
          
          {/* Blob 2 - color 2 */}
          <circle 
            cx="50" 
            cy="50" 
            r="18" 
            fill={color2}
            opacity="0.7"
            className="playbook-orb__blob"
            style={{
              '--blob-duration': `${durations.blob2}s`,
              '--blob-offset': `-${durations.blob2 / 3}s`
            } as React.CSSProperties}
          />
          
          {/* Blob 3 - color 3 */}
          <circle 
            cx="50" 
            cy="50" 
            r="16" 
            fill={color3}
            opacity="0.7"
            className="playbook-orb__blob"
            style={{
              '--blob-duration': `${durations.blob3}s`,
              '--blob-offset': `-${durations.blob3 * 2 / 3}s`
            } as React.CSSProperties}
          />
          
          {/* Additional small blobs for more texture */}
          <circle 
            cx="50" 
            cy="50" 
            r="12" 
            fill={color1}
            opacity="0.6"
            className="playbook-orb__blob playbook-orb__blob--reverse"
            style={{
              '--blob-duration': `${durations.main}s`,
              '--blob-offset': `-${durations.main / 2}s`
            } as React.CSSProperties}
          />
          
          <circle 
            cx="50" 
            cy="50" 
            r="14" 
            fill={color2}
            opacity="0.5"
            className="playbook-orb__blob playbook-orb__blob--reverse"
            style={{
              '--blob-duration': `${durations.main + 2}s`,
              '--blob-offset': `-${durations.main / 4}s`
            } as React.CSSProperties}
          />
        </g>
      </svg>
    </div>
  );
};

export default PlaybookOrb;
